<template>


  <SideBarAsideFix/>

  <div class="content pd-0" style="border: 1px solid #ccc;">
    <div class="content-header">
      <div class="content-search">
        <i data-feather="search"></i>
        <input type="search" class="form-control" placeholder="Search...">
      </div>
      <nav class="nav">
        <a href="" class="nav-link"><i data-feather="help-circle"></i></a>
        <a href="" class="nav-link"><i data-feather="grid"></i></a>
        <a href="" class="nav-link"><i data-feather="align-left"></i></a>
      </nav>
    </div><!-- content-header -->

    <div class="content-body">
      <div class="container pd-x-0">


        <div class="buy-credits-page">

          <!-- ═══ Breadcrumb + Title ═══ -->
          <div class="page-header">
            <div>
              <nav class="breadcrumb-nav">
                <span>SMS</span>
                <span class="sep">/</span>
                <span class="active">Buy Credits</span>
              </nav>
              <h4 class="page-title">Buy SMS Credits</h4>
            </div>
            <router-link to="/sms" class="btn-outline">
              <i class="fas fa-arrow-left"></i> Back to SMS Dashboard
            </router-link>
          </div>

          <!-- ═══ Step Indicator ═══ -->
          <div class="buy-steps" v-if="!purchaseComplete">
            <div class="step-item" :class="{ done: true }">
              <div class="step-num"><i class="fas fa-check" style="font-size:11px;"></i></div>
              <span class="step-label">Select Package</span>
            </div>
            <div class="step-connector"></div>
            <div class="step-item" :class="{ active: !purchaseComplete }">
              <div class="step-num">2</div>
              <span class="step-label">Payment</span>
            </div>
            <div class="step-connector"></div>
            <div class="step-item">
              <div class="step-num">3</div>
              <span class="step-label">Confirmation</span>
            </div>
          </div>

          <!-- ═══ Main Purchase Layout ═══ -->
          <div class="main-grid" v-if="!purchaseComplete">

            <!-- LEFT: Package selection + Payment -->
            <div class="col-left">

              <!-- Balance Strip -->
              <div class="balance-strip">
                <div>
                  <div class="bal-label">Current SMS Balance</div>
                  <div class="bal-value">{{ currentBalance.toLocaleString() }}</div>
                  <div class="bal-sub">credits available</div>
                </div>
                <div class="bal-stat">
                  <div class="bal-label">Total purchased</div>
                  <div class="bal-stat-val">1,000</div>
                  <div class="bal-sub">all time</div>
                </div>
                <div class="bal-stat">
                  <div class="bal-label">Total used</div>
                  <div class="bal-stat-val">550</div>
                  <div class="bal-sub">all time</div>
                </div>
              </div>

              <!-- Package Selection -->
              <div class="card">
                <div class="card-header-bar">
                  <div>
                    <h6 class="card-title">Choose a Package</h6>
                    <p class="card-sub">Select the credit bundle that best suits your sending volume.</p>
                  </div>
                </div>
                <div class="card-body">
                  <div class="pkg-grid">
                    <div
                        v-for="pkg in packages"
                        :key="pkg.name"
                        class="pkg-card"
                        :class="{ selected: selectedPkg && selectedPkg.name === pkg.name, popular: pkg.popular }"
                        @click="selectPackage(pkg)">
                      <div v-if="pkg.popular" class="pkg-badge">Most Popular</div>
                      <div class="pkg-name" :style="pkg.popular ? 'margin-top:10px;' : ''">{{ pkg.name }}</div>
                      <div class="pkg-credits">{{ pkg.sms_credits.toLocaleString() }}</div>
                      <div class="pkg-credits-lbl">credits</div>
                      <div class="pkg-price">£{{ pkg.price.toFixed(2) }}</div>
                      <div class="pkg-rate">£{{ pkg.rate }} / SMS</div>
                    </div>
                  </div>

                  <div class="info-banner">
                    <i class="fas fa-info-circle"></i>
                    <span>Credits never expire. Buy more anytime and they'll be added to your existing balance.</span>
                  </div>
                </div>
              </div>

              <!-- Payment Details -->
              <div class="card">
                <div class="card-header-bar">
                  <div>
                    <h6 class="card-title">Payment Details</h6>
                    <p class="card-sub">Secured by Stripe. Your card details are never stored on our servers.</p>
                  </div>
                  <div class="card-logos">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/0/04/Visa.svg" alt="Visa" height="20">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" alt="MC"
                         height="20">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/f/fa/American_Express_logo_%282018%29.svg"
                         alt="Amex" height="20">
                  </div>
                </div>
                <div class="card-body">
                  <div class="fields-grid">

                    <!-- Card Number -->
                    <div class="field-group full">
                      <label class="field-label">Card Number</label>
                      <div class="card-field-wrapper" :class="{ focused: focusedField === 'number' }">
                        <i :class="cardBrandIcon" class="card-brand-icon"></i>
                        <input
                            type="text"
                            v-model="cardNumber"
                            placeholder="1234  5678  9012  3456"
                            maxlength="19"
                            @input="onCardNumberInput"
                            @focus="focusedField = 'number'"
                            @blur="focusedField = null"
                        >
                        <i class="fas fa-lock lock-icon"></i>
                      </div>
                    </div>

                    <!-- Expiry -->
                    <div class="field-group">
                      <label class="field-label">Expiry Date</label>
                      <div class="card-field-wrapper" :class="{ focused: focusedField === 'expiry' }">
                        <input
                            type="text"
                            v-model="cardExpiry"
                            placeholder="MM / YY"
                            maxlength="7"
                            @input="onExpiryInput"
                            @focus="focusedField = 'expiry'"
                            @blur="focusedField = null"
                        >
                      </div>
                    </div>

                    <!-- CVV -->
                    <div class="field-group">
                      <label class="field-label">CVV / CVC</label>
                      <div class="card-field-wrapper" :class="{ focused: focusedField === 'cvv' }">
                        <input
                            type="text"
                            v-model="cardCvv"
                            placeholder="•••"
                            maxlength="4"
                            @input="cardCvv = cardCvv.replace(/\D/g, '')"
                            @focus="focusedField = 'cvv'"
                            @blur="focusedField = null"
                        >
                        <i class="fas fa-question-circle help-icon" title="3 digits on back of card"></i>
                      </div>
                    </div>

                    <!-- Name on Card -->
                    <div class="field-group full">
                      <label class="field-label">Name on Card</label>
                      <div class="card-field-wrapper" :class="{ focused: focusedField === 'name' }">
                        <input
                            type="text"
                            v-model="cardName"
                            placeholder="Katherine Pechon"
                            @focus="focusedField = 'name'"
                            @blur="focusedField = null"
                        >
                      </div>
                    </div>

                  </div>

                  <!-- Save card -->
                  <label class="save-card-toggle">
                    <input type="checkbox" v-model="saveCard">
                    <span>Save card for future purchases</span>
                  </label>

                </div>
              </div>

            </div>
            <!-- /LEFT -->

            <!-- RIGHT: Order Summary -->
            <div class="col-right">
              <div class="card summary-card">
                <div class="card-header-bar">
                  <h6 class="card-title">Order Summary</h6>
                </div>
                <div class="card-body">

                  <!-- Selected package display -->
                  <div class="summary-pkg-row" v-if="selectedPkg">
                    <div>
                      <div class="field-label">Selected package</div>
                      <div class="summary-pkg-name">{{ selectedPkg.name }}</div>
                    </div>
                    <div class="text-right">
                      <div class="field-label">Credits</div>
                      <div class="summary-pkg-credits">{{ selectedPkg.sms_credits.toLocaleString() }}</div>
                    </div>
                  </div>

                  <!-- Line items -->
                  <div class="order-summary">
                    <div class="line">
                      <span>Package</span>
                      <span v-if="selectedPkg">{{ selectedPkg.name }} — {{ selectedPkg.sms_credits.toLocaleString() }} credits</span>
                    </div>
                    <div class="line">
                      <span>Unit rate</span>
                      <span v-if="selectedPkg">£{{ selectedPkg.rate }} / SMS</span>
                    </div>
                    <div class="line">
                      <span>VAT (0%)</span>
                      <span>£0.00</span>
                    </div>
                    <div class="line total">
                      <span>Total due today</span>
                      <span v-if="selectedPkg">£{{ selectedPkg.price.toFixed(2) }}</span>
                    </div>
                    <div class="line balance-after">
                      <span>Balance after purchase</span>
                      <span v-if="selectedPkg">{{ balanceAfter.toLocaleString() }} credits</span>
                    </div>
                  </div>

                  <!-- Confirm button -->
                  <button class="btn-purchase" :disabled="processing || !selectedPkg" @click="handlePurchase">
                    <span v-if="processing"><i class="fas fa-spinner fa-spin"></i> Processing…</span>
                    <span v-else>
                        <i class="fas fa-lock"></i>
                        Confirm &amp; Pay {{ selectedPkg ? '£' + selectedPkg.price.toFixed(2) : '' }}
                      </span>
                  </button>

                  <p class="secure-note">
                    <i class="fas fa-shield-alt"></i>
                    Payments secured by <strong>Stripe</strong>. You'll receive a receipt by email.
                  </p>

                  <hr class="divider">

                  <!-- Saved Cards -->
                  <div class="section-label">Saved Cards</div>
                  <div class="saved-cards">
                    <div
                        v-for="card in savedCards"
                        :key="card.last4"
                        class="saved-card-row"
                        :class="{ active: selectedSavedCard === card.last4 }"
                        @click="selectedSavedCard = card.last4">
                      <i :class="card.icon" class="card-network-icon"></i>
                      <div class="saved-card-info">
                        <div class="saved-card-name">{{ card.label }} ···· {{ card.last4 }}</div>
                        <div class="saved-card-exp">Expires {{ card.expires }}</div>
                      </div>
                      <span v-if="card.isDefault" class="badge-default">Default</span>
                    </div>
                  </div>

                </div>
              </div>
            </div>
            <!-- /RIGHT -->

          </div>
          <!-- /main grid -->

          <!-- ═══ Success Screen ═══ -->
          <div v-if="purchaseComplete" class="success-screen">
            <div class="success-card">
              <div class="success-icon">
                <i class="fas fa-check"></i>
              </div>
              <h5>Payment Successful</h5>
              <p>
                <strong>{{ successData.sms_credits.toLocaleString() }} credits</strong> have been added to your
                wallet.<br>
                Your new balance is <strong>{{ successData.newBalance.toLocaleString() }} credits</strong>.
              </p>
              <div class="success-stats">
                <div class="success-stat">
                  <div class="field-label">Amount charged</div>
                  <div class="success-stat-val">£{{ successData.amount }}</div>
                </div>
                <div class="success-divider"></div>
                <div class="success-stat">
                  <div class="field-label">Receipt ref</div>
                  <div class="success-stat-ref">{{ successData.ref }}</div>
                </div>
                <div class="success-divider"></div>
                <div class="success-stat">
                  <div class="field-label">New balance</div>
                  <div class="success-stat-bal">{{ successData.newBalance.toLocaleString() }}</div>
                </div>
              </div>
              <div class="success-actions">
                <button class="btn-outline" @click="resetPage">
                  <i class="fas fa-arrow-left"></i> Buy More Credits
                </button>
                <router-link to="/sms" class="btn-primary-sm">
                  <i class="fas fa-paper-plane"></i> Go to SMS Dashboard
                </router-link>
              </div>
            </div>
          </div>

        </div>


      </div><!-- container -->
    </div>

  </div>


</template>

<script>
import SideBarAsideFix from "../components/SideBarAsideFix.vue";
import footerScriptMixin from "@/mixins/footer-script";

export default {
  name: 'SmsBuyCredits',
  components: {SideBarAsideFix},
  mixins: [footerScriptMixin],
  props: {
    report: {type: String},
    purchase_id: {type: String}
  },
  data() {
    return {
      // Wallet
      currentBalance: 450,

      // Packages
      packages: [],

      // Selected package (default: Growth)
      selectedPkg: null,

      // Payment fields
      cardNumber: '',
      cardExpiry: '',
      cardCvv: '',
      cardName: '',
      saveCard: true,
      focusedField: null,

      // Saved cards
      savedCards: [
        {label: 'Visa', last4: '4242', expires: '08 / 26', icon: 'fab fa-cc-visa', isDefault: true},
        {label: 'Mastercard', last4: '8853', expires: '03 / 27', icon: 'fab fa-cc-mastercard', isDefault: false},
      ],
      selectedSavedCard: '4242',

      // Purchase state
      processing: false,
      purchaseComplete: false,
      successData: {sms_credits: 0, newBalance: 0, amount: '0.00', ref: ''},
    }
  },

  computed: {
    balanceAfter() {
      if (!this.selectedPkg) return this.currentBalance
      return this.currentBalance + this.selectedPkg.sms_credits
    },

    cardBrandIcon() {
      const v = this.cardNumber.replace(/\s/g, '')
      if (/^4/.test(v)) return 'fab fa-cc-visa card-brand-icon brand-visa'
      if (/^5[1-5]/.test(v)) return 'fab fa-cc-mastercard card-brand-icon brand-mc'
      if (/^3[47]/.test(v)) return 'fab fa-cc-amex card-brand-icon brand-amex'
      return 'fas fa-credit-card card-brand-icon'
    },
  },

  created() {
    if(this.report && this.purchase_id){
      alert('Fetching packages...'+ this.report + ' ' + this.purchase_id);
    }

    this.fetchPackages();
    this.footerScript();
    this.asideBarScript();
  },

  methods: {
    async fetchPackages() {
      try {
        const response = await this.$axios.get(`${this.subscriptionServer}/plans`);
        this.packages = response.data._embedded?.plans || [];
        if (this.packages && this.packages.length > 0) {
          const growth = this.packages.find(p => p.name === 'Growth');  // Default selection: Growth
          this.selectedPkg = growth || this.packages[0];
        }
      } catch (error) {
        console.error('Error fetching plans:', error);
      }
    },

    selectPackage(pkg) {
      this.selectedPkg = pkg
    },

    onCardNumberInput() {
      const digits = this.cardNumber.replace(/\D/g, '').substring(0, 16)
      this.cardNumber = digits.replace(/(.{4})/g, '$1  ').trim()
    },

    onExpiryInput() {
      const v = this.cardExpiry.replace(/\D/g, '')
      if (v.length >= 2) {
        this.cardExpiry = v.substring(0, 2) + ' / ' + v.substring(2, 4)
      } else {
        this.cardExpiry = v
      }
    },

    async handlePurchase() {
      if (!this.selectedPkg || this.processing) return;
      this.processing = true;

      const payload = {
        organization_id: "6308b2c6c6aae2000171b3c9",
        plan_id: this.selectedPkg.id,
        success_redirect_url: "http://localhost:8080/sms/credits?report=success&purchase_id=",
        cancel_redirect_url: "http://localhost:8080/sms/credits?report=cancel&purchase_id="
      };

      try {
        const response = await this.$axios.post(`${this.subscriptionServer}/payments/checkout`, payload);
        if (response.data && response.data.checkout_url) {
          window.open(response.data.checkout_url, '_parent');
          this.processing = false;
        } else {
          // If no checkout_url, fallback to local success simulation
          alert('No checkout URL received. Using local success simulation.');
          this.simulateSuccess();
        }
      } catch (error) {
        console.error('Purchase error:', error);
        this.processing = false;
        // Optionally show an error message
      }
    },

    simulateSuccess() {
      setTimeout(() => {
        const newBalance = this.currentBalance + this.selectedPkg.sms_credits
        this.successData = {
          sms_credits: this.selectedPkg.sms_credits,
          newBalance: newBalance,
          amount: this.selectedPkg.price.toFixed(2),
          ref: 'pi_' + Math.random().toString(36).substring(2, 12) + '…',
        }
        this.currentBalance = newBalance
        this.processing = false
        this.purchaseComplete = true
      }, 1800)
    },

    resetPage() {
      this.purchaseComplete = false
      this.cardNumber = ''
      this.cardExpiry = ''
      this.cardCvv = ''
      this.cardName = ''
      this.selectedPkg = this.packages.find(p => p.name === 'Growth')
    },
  },
}
</script>

<style scoped>
@import '../../assets/css/dashforge.dashboard.css';
@import '../../assets/lib/remixicon/fonts/remixicon.css';
@import '../../assets/lib/select2/css/select2.min.css';


.buy-credits-page {
  padding: 20px;
  max-width: 1140px;
  margin: 0 auto;
  font-family: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  color: #1c273c;
  background: #f4f5f8;
  min-height: 100vh;
}

/* ═══ Page Header ═══ */
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
}

.breadcrumb-nav {
  font-size: 12px;
  color: #8392a5;
  margin-bottom: 6px;
}

.breadcrumb-nav .sep {
  margin: 0 6px;
}

.breadcrumb-nav .active {
  color: #596882;
  font-weight: 500;
}

.page-title {
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.3px;
}

/* ═══ Buttons ═══ */
.btn-outline {
  background: #fff;
  border: 1px solid #d5d9e3;
  border-radius: 6px;
  padding: 7px 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  color: #596882;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  transition: border-color .15s, color .15s;
}

.btn-outline:hover {
  border-color: #0168fa;
  color: #0168fa;
}

.btn-primary-sm {
  background: #0168fa;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 7px 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  transition: background .15s;
}

.btn-primary-sm:hover {
  background: #0148c0;
}

.btn-purchase {
  width: 100%;
  background: #0168fa;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background .15s;
}

.btn-purchase:hover:not(:disabled) {
  background: #0148c0;
}

.btn-purchase:disabled {
  opacity: .55;
  cursor: not-allowed;
}

/* ═══ Steps ═══ */
.buy-steps {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #c0ccda;
  text-transform: uppercase;
  letter-spacing: .04em;
  flex: 1;
}

.step-item.active {
  color: #0168fa;
}

.step-item.done {
  color: #10b981;
}

.step-num {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  background: #e5e9f2;
  color: #8392a5;
  flex-shrink: 0;
}

.step-item.active .step-num {
  background: #0168fa;
  color: #fff;
}

.step-item.done .step-num {
  background: #10b981;
  color: #fff;
}

.step-connector {
  height: 1px;
  background: #e5e9f2;
  flex: 1;
  margin: 0 6px;
}

.step-label {
  display: none;
}

@media (min-width: 576px) {
  .step-label {
    display: inline;
  }
}

/* ═══ Main grid ═══ */
.main-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
}

@media (min-width: 992px) {
  .main-grid {
    grid-template-columns: 1fr 340px;
  }
}

.col-left {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.col-right {
  position: relative;
}

/* ═══ Balance Strip ═══ */
.balance-strip {
  background: linear-gradient(135deg, #0168fa 0%, #0658d8 100%);
  border-radius: 8px;
  padding: 16px 20px;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.bal-label {
  font-size: 11px;
  opacity: .8;
  text-transform: uppercase;
  letter-spacing: .06em;
  margin-bottom: 2px;
}

.bal-value {
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
}

.bal-sub {
  font-size: 12px;
  opacity: .75;
  margin-top: 3px;
}

.bal-stat {
  text-align: right;
}

.bal-stat-val {
  font-size: 18px;
  font-weight: 700;
}

/* ═══ Cards ═══ */
.card {
  background: #fff;
  border: 1px solid #e5e9f2;
  border-radius: 8px;
  overflow: hidden;
}

.card-header-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e9f2;
  flex-wrap: wrap;
  gap: 12px;
}

.card-title {
  font-size: 13px;
  font-weight: 600;
  margin: 0;
}

.card-sub {
  font-size: 12px;
  color: #8392a5;
  margin: 3px 0 0;
}

.card-body {
  padding: 20px;
}

.card-logos {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* ═══ Package grid ═══ */
.pkg-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

@media (min-width: 768px) {
  .pkg-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

.pkg-card {
  border: 2px solid #e5e9f2;
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: border-color .15s, box-shadow .15s;
  position: relative;
  background: #fff;
  text-align: left;
}

.pkg-card:hover {
  border-color: #0168fa;
  box-shadow: 0 2px 12px rgba(1, 104, 250, .12);
}

.pkg-card.selected {
  border-color: #0168fa;
  background: #f5f9ff;
  box-shadow: 0 2px 16px rgba(1, 104, 250, .18);
}

.pkg-card.selected::after {
  content: '\f058';
  font-family: 'Font Awesome 6 Free';
  font-weight: 900;
  position: absolute;
  top: 12px;
  right: 14px;
  color: #0168fa;
  font-size: 16px;
}

.pkg-badge {
  position: absolute;
  top: -1px;
  left: 50%;
  transform: translateX(-50%);
  background: #0168fa;
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: .05em;
  text-transform: uppercase;
  padding: 3px 12px;
  border-radius: 0 0 6px 6px;
  white-space: nowrap;
}

.pkg-name {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: #8392a5;
}

.pkg-credits {
  font-size: 28px;
  font-weight: 700;
  color: #1c273c;
  line-height: 1;
  margin: 6px 0 2px;
}

.pkg-credits-lbl {
  font-size: 12px;
  color: #8392a5;
  margin-bottom: 4px;
}

.pkg-price {
  font-size: 18px;
  font-weight: 700;
  color: #0168fa;
  margin-top: 8px;
}

.pkg-rate {
  font-size: 11px;
  color: #8392a5;
  margin-top: 2px;
}

/* Info banner */
.info-banner {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 13px;
  color: #1e40af;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.info-banner i {
  font-size: 14px;
  flex-shrink: 0;
  margin-top: 1px;
}

/* ═══ Payment fields ═══ */
.fields-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 14px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-group.full {
  grid-column: 1 / -1;
}

.field-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .08em;
  color: #8392a5;
}

.card-field-wrapper {
  border: 1px solid #e5e9f2;
  border-radius: 6px;
  padding: 9px 12px;
  background: #fff;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: border-color .15s, box-shadow .15s;
}

.card-field-wrapper.focused {
  border-color: #0168fa;
  box-shadow: 0 0 0 3px rgba(1, 104, 250, .12);
}

.card-field-wrapper input {
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #1c273c;
  flex: 1;
  min-width: 0;
  font-family: inherit;
}

.card-field-wrapper input::placeholder {
  color: #c0ccda;
}

.card-brand-icon {
  font-size: 22px;
  color: #8392a5;
  flex-shrink: 0;
}

.brand-visa {
  color: #1a1f71;
}

.brand-mc {
  color: #eb001b;
}

.brand-amex {
  color: #007bc1;
}

.lock-icon {
  color: #c0ccda;
  font-size: 13px;
  flex-shrink: 0;
}

.help-icon {
  color: #c0ccda;
  font-size: 13px;
  flex-shrink: 0;
  cursor: help;
}

.save-card-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #596882;
  cursor: pointer;
  user-select: none;
}

.save-card-toggle input {
  cursor: pointer;
}

/* ═══ Order Summary ═══ */
.summary-card {
  position: sticky;
  top: 20px;
}

.summary-pkg-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 14px;
  margin-bottom: 14px;
  border-bottom: 1px solid #e5e9f2;
}

.summary-pkg-name {
  font-size: 16px;
  font-weight: 600;
  color: #1c273c;
  margin-top: 3px;
}

.summary-pkg-credits {
  font-size: 24px;
  font-weight: 700;
  color: #0168fa;
  line-height: 1;
  margin-top: 3px;
}

.text-right {
  text-align: right;
}

.order-summary {
  background: #f5f9ff;
  border: 1px solid #d6e4ff;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 16px;
}

.line {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #596882;
  padding: 5px 0;
  border-bottom: 1px solid #e2eaff;
}

.line:last-child {
  border-bottom: none;
}

.line.total {
  font-weight: 700;
  font-size: 15px;
  color: #1c273c;
  padding-top: 10px;
  margin-top: 4px;
}

.line.balance-after {
  color: #0168fa;
  font-weight: 600;
  border-top: 1px dashed #d6e4ff;
  padding-top: 10px;
  margin-top: 6px;
}

.secure-note {
  font-size: 12px;
  color: #8392a5;
  text-align: center;
  margin: 10px 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

.divider {
  border: none;
  border-top: 1px solid #e5e9f2;
  margin: 18px 0;
}

.section-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .08em;
  color: #8392a5;
  margin-bottom: 10px;
}

/* ═══ Saved Cards ═══ */
.saved-cards {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.saved-card-row {
  display: flex;
  align-items: center;
  padding: 10px 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background .1s;
  gap: 10px;
}

.saved-card-row:hover {
  background: #f8f9fc;
}

.saved-card-row.active {
  background: #f5f9ff;
}

.card-network-icon {
  font-size: 22px;
  flex-shrink: 0;
  color: #8392a5;
}

.saved-card-info {
  flex: 1;
}

.saved-card-name {
  font-size: 13px;
  font-weight: 600;
  color: #1c273c;
}

.saved-card-exp {
  font-size: 12px;
  color: #8392a5;
}

.badge-default {
  background: #e6f9f2;
  color: #0d7a52;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
}

/* ═══ Success Screen ═══ */
.success-screen {
  display: flex;
  justify-content: center;
  padding: 40px 16px;
}

.success-card {
  background: #fff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  padding: 40px;
  text-align: center;
  max-width: 560px;
  width: 100%;
}

.success-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #e6f9f2;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}

.success-icon i {
  font-size: 28px;
  color: #10b981;
}

.success-card h5 {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
}

.success-card p {
  font-size: 14px;
  color: #596882;
  margin-bottom: 20px;
  line-height: 1.6;
}

.success-stats {
  background: #f4f5f8;
  border-radius: 8px;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.success-stat {
  text-align: center;
}

.success-stat-val {
  font-size: 18px;
  font-weight: 700;
  color: #1c273c;
}

.success-stat-ref {
  font-size: 13px;
  font-family: 'Roboto Mono', monospace;
  color: #596882;
}

.success-stat-bal {
  font-size: 18px;
  font-weight: 700;
  color: #0168fa;
}

.success-divider {
  width: 1px;
  height: 36px;
  background: #e5e9f2;
  flex-shrink: 0;
}

.success-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
}

/* ═══ Responsive ═══ */
@media (max-width: 767px) {
  .pkg-credits {
    font-size: 22px !important;
  }

  .bal-value {
    font-size: 22px !important;
  }

  .success-card {
    padding: 24px 16px;
  }
}
</style>
